import { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, ShoppingCart, ArrowLeft, CheckCircle2, Heart } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api } from "@/api/client";
import { normalizeImageUrl } from "@/lib/imageUrl";
import Reveal from "@/components/Reveal";

const priceNum = (p) => typeof p === "number" ? p : parseInt(String(p).replace(/[^\d]/g, ""), 10) || 0;

export default function ProductDetail() {
  const { id } = useParams();
  const { add } = useCart();
  const [qty, setQty] = useState(1);
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/products/${id}`)
      .then(({ data }) => {
        if (!alive) return;
        setProduct({ ...data, image: normalizeImageUrl(data.image) });
        return api.get("/products", { params: { limit: 24 } }).then(({ data: list }) => {
          if (!alive) return;
          const all = Array.isArray(list) ? list : (list.items || []);
          setRelated(
            all
              .filter((p) => p.category === data.category && p.id !== data.id)
              .slice(0, 4)
              .map((p) => ({ ...p, image: normalizeImageUrl(p.image) }))
          );
        });
      })
      .catch((err) => {
        // Only mark as not-found if the *primary* product fetch failed.
        if (alive && err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  if (notFound) return <Navigate to="/products" replace />;
  if (loading || !product) return <div className="bg-[var(--bg)] pt-[140px] pb-20 min-h-screen text-center text-[var(--ink-3)] kbd text-xs">Loading…</div>;

  const onAdd = () => {
    add({ ...product, price: priceNum(product.price) }, qty);
    toast.success(`${product.name} (×${qty}) added to cart.`);
  };

  return (
    <div data-testid="page-product-detail" className="bg-[var(--bg)] pt-[110px] md:pt-[120px] pb-20">
      <div className="max-w-[1480px] mx-auto px-4 md:px-10">
        <Link to="/products" data-testid="product-back" className="kbd link-underline text-[var(--ink-3)] inline-flex items-center gap-2">
          <ArrowLeft size={14} /> Back to Shop
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 mt-6">
          <Reveal>
            <div className="aspect-square bg-white rounded-2xl overflow-hidden border border-[var(--line-soft)]">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="overline overline-green">Native Touch · Pantry</div>
            <h1 className="font-serif-display text-3xl md:text-5xl mt-3 leading-tight font-semibold text-[var(--ink)]">{product.name}</h1>
            <div className="kbd text-[var(--ink-3)] mt-3">{product.weight}</div>

            <div className="flex flex-wrap gap-1.5 mt-5">
              {(product.tags || []).map((t) => (
                <span key={t} className="text-[10px] uppercase tracking-widest border border-[var(--line)] text-[var(--ink-2)] px-2.5 py-1 rounded-full">{t}</span>
              ))}
            </div>

            <div className="mt-7 flex items-baseline gap-3">
              <span className="font-serif-display text-4xl md:text-5xl font-semibold text-[var(--green)]">₹{priceNum(product.price)}</span>
              <span className="text-sm text-[var(--ink-3)]">incl. of all taxes</span>
            </div>

            <p className="mt-6 text-[var(--ink-2)] leading-relaxed max-w-lg">
              {product.description || "Crafted with single-origin millets, milled in small batches and packed without preservatives. Quick to cook, easy to love — built for everyday kitchens that don't compromise on what's inside."}
            </p>

            <div className="mt-7 border-t border-[var(--line-soft)] pt-7">
              <div className="kbd text-[var(--ink-3)] mb-3">Quantity</div>
              <div className="flex items-center gap-4">
                <div className="inline-flex items-center border border-[var(--line)] rounded-full bg-white">
                  <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2.5 hover:text-[var(--green)]"><Minus size={14} /></button>
                  <span data-testid="qty-value" className="px-4 font-semibold text-[var(--ink)] min-w-[40px] text-center">{qty}</span>
                  <button data-testid="qty-plus" onClick={() => setQty((q) => q + 1)} className="px-3 py-2.5 hover:text-[var(--green)]"><Plus size={14} /></button>
                </div>
                <button data-testid="product-wishlist" onClick={() => toast.info("Saved to wishlist.")} className="w-11 h-11 rounded-full border border-[var(--line)] flex items-center justify-center hover:border-[var(--green)] hover:text-[var(--green)] transition">
                  <Heart size={16} />
                </button>
              </div>
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <button data-testid="product-add-cart" onClick={onAdd} className="btn-primary"><ShoppingCart size={14} /> Add to Cart</button>
              <Link to="/cart" data-testid="product-go-cart" className="btn-outline">View Cart →</Link>
            </div>

            <ul className="mt-9 space-y-2.5 text-sm text-[var(--ink-2)]">
              {["Free delivery on orders above ₹500", "Easy returns within 7 days", "100% authentic, sealed by Native Touch"].map((t) => (
                <li key={t} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[var(--gold)]" /> {t}</li>
              ))}
            </ul>
          </Reveal>
        </div>

        {related.length > 0 && (
          <div className="mt-20">
            <h3 className="font-serif-display text-2xl md:text-3xl font-semibold mb-6">You may also like</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {related.map((p) => (
                <Link key={p.id} to={`/products/${p.id}`} data-testid={`related-product-${p.id}`} className="card-soft block overflow-hidden">
                  <div className="aspect-square bg-white"><img src={p.image} alt={p.name} className="w-full h-full object-cover" /></div>
                  <div className="p-4">
                    <h4 className="font-serif-display text-base font-semibold leading-tight line-clamp-2">{p.name}</h4>
                    <div className="mt-2 font-serif-display text-lg text-[var(--green)] font-semibold">₹{priceNum(p.price)}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
